import { visualRoute } from '../utils/routePresentation'
import { FilterSelect } from './FilterToolbar'
/**
 * Arquivo: src/components/AppLayout.tsx
 * Versão: 6.0.0
 * Data: 17/09/2026
 */
import { Eye, EyeOff, LogOut, Menu, Moon, Sun, Search, Command, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Sidebar from './Sidebar'
import MobileBottomNav from './MobileBottomNav'
import { QuickCreateProvider } from '../context/QuickCreateContext'
import { neonAuth } from '../services/neonAuth'
import { PeriodProvider, usePeriod } from '../context/PeriodContext'
import { listarAnosDisponiveis, obterBaseAtiva } from '../services/financeiro'

function LayoutContent({ children }: PropsWithChildren) {
  const location = useLocation()
  const dashboardPreview=true
  const presentationPath = visualRoute(location.pathname, location.search, location.state)
  const navigate = useNavigate()
  const { periodo, setPeriodo } = usePeriod()
  const [privado, setPrivado] = useState(() => localStorage.getItem('cf_privacidade') === '1')
  const [temaPwa, setTemaPwa] = useState<'light' | 'dark'>(() => localStorage.getItem('cf_pwa_theme') === 'dark' ? 'dark' : 'light')
  const [recolhido, setRecolhido] = useState(() => localStorage.getItem('cf_sidebar_recolhido') === '1')
  const [menuMobileAberto, setMenuMobileAberto] = useState(false)
  const [buscaAberta, setBuscaAberta] = useState(false)
  const [termoBusca, setTermoBusca] = useState('')
  const [usuario, setUsuario] = useState<{ nome: string; email: string } | null>(null)
  const hoje = useMemo(() => new Date(), [])
  const mesSelecionado = periodo === 'ALL' ? 'ALL' : periodo.slice(5, 7)
  const anoDoPeriodo = periodo === 'ALL' ? String(hoje.getFullYear()) : periodo.slice(0, 4)
  const [anoSelecionado, setAnoSelecionado] = useState(anoDoPeriodo)

  const meses = useMemo(() => [
    { value: 'ALL', label: 'Todos' },
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' },
  ], [])

  const [anos, setAnos] = useState<string[]>([anoDoPeriodo])
  const ultimaAtualizacaoAnos = useRef(0)
  const seletorPeriodoRef = useRef<HTMLDetailsElement>(null)
  const ultimoMesRef = useRef(periodo === 'ALL'
    ? `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`
    : periodo)
  const abrirNoProximoToqueRef = useRef(false)

  useEffect(() => {
    if (periodo !== 'ALL') ultimoMesRef.current = periodo
  }, [periodo])

  const fecharSeletorPeriodo = () => {
    abrirNoProximoToqueRef.current = false
    if (seletorPeriodoRef.current) seletorPeriodoRef.current.open = false
  }

  useEffect(() => {
    const aoTocarFora = (event: PointerEvent) => {
      if (event.target instanceof Node && !seletorPeriodoRef.current?.contains(event.target)) fecharSeletorPeriodo()
    }
    const aoPressionarTecla = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !seletorPeriodoRef.current?.open) return
      fecharSeletorPeriodo()
      seletorPeriodoRef.current.querySelector('summary')?.focus()
    }
    const aoMudarFoco = (event: FocusEvent) => {
      if (event.target instanceof Node && !seletorPeriodoRef.current?.contains(event.target)) fecharSeletorPeriodo()
    }
    document.addEventListener('pointerdown', aoTocarFora)
    document.addEventListener('keydown', aoPressionarTecla)
    document.addEventListener('focusin', aoMudarFoco)
    return () => {
      document.removeEventListener('pointerdown', aoTocarFora)
      document.removeEventListener('keydown', aoPressionarTecla)
      document.removeEventListener('focusin', aoMudarFoco)
    }
  }, [])

  useEffect(fecharSeletorPeriodo, [location.pathname])

  const carregarAnosLancamentos = useCallback(async (forcar = false) => {
    const agora = Date.now()
    if (!forcar && agora - ultimaAtualizacaoAnos.current < 5 * 60 * 1000) return
    try {
      const base = await obterBaseAtiva()
      if (!base?.id) return
      const anosComLancamento = await listarAnosDisponiveis(base.id)
      ultimaAtualizacaoAnos.current = Date.now()
      setAnos(anosComLancamento)
      if (anosComLancamento.length && !anosComLancamento.includes(anoSelecionado)) {
        const anoMaisRecente = anosComLancamento[anosComLancamento.length - 1]
        setAnoSelecionado(anoMaisRecente)
        if (mesSelecionado !== 'ALL') setPeriodo(`${anoMaisRecente}-${mesSelecionado}`)
      }
    } catch (error) {
      console.warn('[Período] Não foi possível carregar os anos com lançamentos.', error)
    }
  }, [anoSelecionado, mesSelecionado, setPeriodo])

  useLayoutEffect(() => {
    document.body.classList.toggle('pwa-dark-theme', temaPwa === 'dark')
    localStorage.setItem('cf_pwa_theme', temaPwa)
    return () => document.body.classList.remove('pwa-dark-theme')
  }, [temaPwa])

  useEffect(() => {
    document.body.classList.toggle('privacy-mode', privado)
    localStorage.setItem('cf_privacidade', privado ? '1' : '0')
    return () => document.body.classList.remove('privacy-mode')
  }, [privado])

  useEffect(() => {
    localStorage.setItem('cf_sidebar_recolhido', recolhido ? '1' : '0')
  }, [recolhido])

  useEffect(() => {
    document.body.classList.toggle('mobile-menu-open', menuMobileAberto)
    return () => document.body.classList.remove('mobile-menu-open')
  }, [menuMobileAberto])

  useEffect(() => {
    // Cada módulo começa no topo, como esperado em uma navegação de aplicativo.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname])

  useEffect(() => {
    const fecharAoAmpliar = () => {
      if (window.innerWidth > 1024) setMenuMobileAberto(false)
    }
    window.addEventListener('resize', fecharAoAmpliar)
    return () => window.removeEventListener('resize', fecharAoAmpliar)
  }, [])

  useEffect(() => {
    let ativo=true
    neonAuth.getSession().then(({user})=>{
      if(!ativo||!user)return
      const email=String(user.email||'')
      const nome=String(user.name||email.split('@')[0]||'Usuário')
      setUsuario({nome,email})
    }).catch(()=>{})
    return()=>{ativo=false}
  },[])

  useEffect(() => {
    if (periodo !== 'ALL') setAnoSelecionado(periodo.slice(0, 4))
  }, [periodo])

  useEffect(() => {
    void carregarAnosLancamentos(true)
    const aoRetomar = () => { void carregarAnosLancamentos(false) }
    window.addEventListener('focus', aoRetomar)
    return () => window.removeEventListener('focus', aoRetomar)
  }, [carregarAnosLancamentos])

  const alterarMes = (mes: string) => {
    fecharSeletorPeriodo()
    if (mes === 'ALL') {
      setPeriodo('ALL')
      return
    }
    setPeriodo(`${anoSelecionado}-${mes}`)
  }

  const alterarAno = (ano: string) => {
    setAnoSelecionado(ano)
    if (mesSelecionado !== 'ALL') setPeriodo(`${ano}-${mesSelecionado}`)
  }

  const moverPeriodo = (delta: number) => {
    fecharSeletorPeriodo()
    const baseData = periodo === 'ALL'
      ? new Date(hoje.getFullYear(), hoje.getMonth(), 1)
      : new Date(Number(periodo.slice(0, 4)), Number(periodo.slice(5, 7)) - 1, 1)
    baseData.setMonth(baseData.getMonth() + delta)
    const novoAno = String(baseData.getFullYear())
    const novoMes = String(baseData.getMonth() + 1).padStart(2, '0')
    setAnoSelecionado(novoAno)
    setPeriodo(`${novoAno}-${novoMes}`)
  }

  const periodoPwaLabel = useMemo(() => {
    if (periodo === 'ALL') return 'Todos os meses'
    const [ano, mes] = periodo.split('-').map(Number)
    return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' })
      .format(new Date(ano, mes - 1, 1))
      .replace(/^./, letra => letra.toUpperCase())
  }, [periodo])

  const sair = async () => {
    await neonAuth.signOut()
  }

  const routeTone = presentationPath.startsWith('/pagar')
    ? 'route-expense'
    : presentationPath.startsWith('/receber')
      ? 'route-income'
      : presentationPath.startsWith('/transferencias')
        ? 'route-transfer'
        : presentationPath.startsWith('/cartoes')
          ? 'route-card'
          : presentationPath.startsWith('/investimentos')
            ? 'route-investment'
            : presentationPath.startsWith('/auditoria')
              ? 'route-primary'
              : 'route-primary'


  const routeInfo = useMemo(() => {
    const routes = [
      { test: (path: string) => path === '/', eyebrow: 'Visão geral', title: 'Dashboard' },
      { test: (path: string) => path.startsWith('/pagar'), eyebrow: 'Movimentações', title: 'Saídas' },
      { test: (path: string) => path.startsWith('/receber'), eyebrow: 'Movimentações', title: 'Entradas' },
      { test: (path: string) => path.startsWith('/lancamentos'), eyebrow: 'Movimentações', title: 'Lançamentos' },
      { test: (path: string) => path.startsWith('/transferencias'), eyebrow: 'Movimentações', title: 'Transferências' },
      { test: (path: string) => path.startsWith('/cartoes'), eyebrow: 'Crédito e faturas', title: 'Cartões de Crédito' },
      { test: (path: string) => path.startsWith('/investimentos'), eyebrow: 'Patrimônio', title: 'Investimentos' },
      { test: (path: string) => path.startsWith('/recorrencias'), eyebrow: 'Planejamento', title: 'Recorrências' },
      { test: (path: string) => path.startsWith('/orcamentos'), eyebrow: 'Planejamento', title: 'Orçamentos' },
      { test: (path: string) => path.startsWith('/metas'), eyebrow: 'Planejamento', title: 'Metas' },
      { test: (path: string) => path.startsWith('/contas'), eyebrow: 'Cadastros', title: 'Contas e Bancos' },
      { test: (path: string) => path.startsWith('/plano-contas'), eyebrow: 'Cadastros', title: 'Plano de Contas' },
      { test: (path: string) => path.startsWith('/relatorios'), eyebrow: 'Análises', title: 'Relatórios' },
      { test: (path: string) => path.startsWith('/fiscal'), eyebrow: 'Análises', title: 'Apuração Fiscal' },
      { test: (path: string) => path.startsWith('/conciliacao'), eyebrow: 'Análises', title: 'Conciliação' },
      { test: (path: string) => path.startsWith('/administracao'), eyebrow: 'Sistema', title: 'Administração' },
      { test: (path: string) => path.startsWith('/auditoria'), eyebrow: 'Sistema', title: 'Auditoria' },
      { test: (path: string) => path.startsWith('/configuracoes'), eyebrow: 'Sistema', title: 'Configurações' },
    ]
    return routes.find(route => route.test(presentationPath)) || { eyebrow: 'Controle Financeiro', title: 'Painel' }
  }, [presentationPath])

  const atalhosBusca = useMemo(() => [
    { label: 'Dashboard', detail: 'Visão geral', path: '/' },
    { label: 'Saídas', detail: 'Contas e compromissos a pagar', path: '/pagar' },
    { label: 'Entradas', detail: 'Recebimentos e valores a receber', path: '/receber' },
    { label: 'Lançamentos', detail: 'Todos os movimentos', path: '/lancamentos' },
    { label: 'Transferências', detail: 'Mover valores entre contas', path: '/transferencias' },
    { label: 'Cartões de Crédito', detail: 'Compras, faturas e parcelas', path: '/cartoes' },
    { label: 'Investimentos', detail: 'Patrimônio e aplicações', path: '/investimentos' },
    { label: 'Recorrências', detail: 'Lançamentos recorrentes', path: '/recorrencias' },
    { label: 'Orçamentos', detail: 'Planejamento por categoria', path: '/orcamentos' },
    { label: 'Metas', detail: 'Objetivos financeiros', path: '/metas' },
    { label: 'Contas e Bancos', detail: 'Saldos e cadastros', path: '/contas' },
    { label: 'Relatórios', detail: 'Análises financeiras', path: '/relatorios' },
    { label: 'Conciliação', detail: 'Conferência de movimentos', path: '/conciliacao' },
  ], [])
  const resultadosBusca = useMemo(() => {
    const termo = termoBusca.trim().toLocaleLowerCase('pt-BR')
    if (!termo) return atalhosBusca.slice(0, 7)
    return atalhosBusca.filter(item => `${item.label} ${item.detail}`.toLocaleLowerCase('pt-BR').includes(termo)).slice(0, 8)
  }, [atalhosBusca, termoBusca])
  const abrirAtalho = (path: string) => {
    setBuscaAberta(false)
    setTermoBusca('')
    navigate(path)
  }

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setBuscaAberta(v => !v)
      } else if (event.key === 'Escape') {
        setBuscaAberta(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className={`app-shell ${dashboardPreview?'ff-dashboard-shell ':''}${routeTone}${recolhido ? ' sidebar-collapsed' : ''}${menuMobileAberto ? ' mobile-menu-is-open' : ''}`}>
      <Sidebar
        recolhido={recolhido}
        onToggle={() => setRecolhido(v => !v)}
        usuario={usuario}
        mobileAberto={menuMobileAberto}
        onMobileClose={() => setMenuMobileAberto(false)}
      />
      <main className="main-area">
        <header className="ff-dash-chrome"><div className="ff-dash-chrome-title"><span>FF CONTROLE FINANCEIRO</span><strong>{routeInfo.title}</strong></div><div className="ff-dash-chrome-tools"><div className="ff-dash-period"><button aria-label="Mês anterior" onClick={()=>moverPeriodo(-1)}><ChevronLeft size={18}/></button><details ref={seletorPeriodoRef} className="ff-dash-period-picker"><summary onClick={event => {
          if (seletorPeriodoRef.current?.open) return
          if (periodo === 'ALL') {
            event.preventDefault()
            abrirNoProximoToqueRef.current = true
            setPeriodo(ultimoMesRef.current)
          } else if (abrirNoProximoToqueRef.current) {
            abrirNoProximoToqueRef.current = false
            // O terceiro toque mantém a abertura nativa e acessível do details.
          } else {
            event.preventDefault()
            ultimoMesRef.current = periodo
            setPeriodo('ALL')
          }
        }} title={periodo === 'ALL' ? 'Voltar ao mês anterior' : abrirNoProximoToqueRef.current ? 'Selecionar mês e ano' : 'Mostrar todos os meses'}>{periodoPwaLabel}</summary><div><label>Mês<select value={mesSelecionado} onChange={e=>alterarMes(e.target.value)} aria-label="Selecionar mês">{meses.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}</select></label><label>Ano<select value={anoSelecionado} onChange={e=>alterarAno(e.target.value)} aria-label="Selecionar ano">{anos.map(ano=><option key={ano} value={ano}>{ano}</option>)}</select></label><button onClick={()=>{fecharSeletorPeriodo();setPeriodo(periodo==='ALL'?`${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}`:'ALL')}}>{periodo==='ALL'?'Mês atual':'Todos os meses'}</button></div></details><button aria-label="Próximo mês" onClick={()=>moverPeriodo(1)}><ChevronRight size={18}/></button></div><button aria-label={temaPwa==='dark'?'Usar tema claro':'Usar tema escuro'} onClick={()=>setTemaPwa(v=>v==='dark'?'light':'dark')}>{temaPwa==='dark'?<Sun size={18}/>:<Moon size={18}/>}</button><button aria-label={privado?'Mostrar informações':'Ocultar informações'} onClick={()=>setPrivado(v=>!v)}>{privado?<EyeOff size={18}/>:<Eye size={18}/>}</button><button aria-label="Buscar ou navegar" onClick={()=>setBuscaAberta(true)}><Search size={18}/></button><button className="ff-chrome-logout" aria-label="Sair" onClick={sair}><LogOut size={18}/></button></div></header>
        {buscaAberta && <div className="command-palette-layer" role="presentation" onMouseDown={() => setBuscaAberta(false)}>
          <section className="command-palette" role="dialog" aria-modal="true" aria-label="Busca rápida" onMouseDown={event => event.stopPropagation()}>
            <div className="command-search"><Search size={18}/><input autoFocus value={termoBusca} onChange={event => setTermoBusca(event.target.value)} placeholder="Buscar módulo ou ação..." /><kbd>ESC</kbd></div>
            <div className="command-results">
              <span className="command-section-label">{termoBusca ? 'Resultados' : 'Acesso rápido'}</span>
              {resultadosBusca.map(item => <button key={item.path} type="button" onClick={() => abrirAtalho(item.path)}><span><strong>{item.label}</strong><small>{item.detail}</small></span><ArrowRight size={16}/></button>)}
              {!resultadosBusca.length && <div className="command-empty">Nenhum módulo encontrado.</div>}
            </div>
          </section>
        </div>}
        <div className="content-area">{children}</div>
        <MobileBottomNav onLogout={sair} />
      </main>
    </div>
  )
}

export default function AppLayout({ children }: PropsWithChildren) {
  return <PeriodProvider><QuickCreateProvider><LayoutContent>{children}</LayoutContent></QuickCreateProvider></PeriodProvider>
}

